import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate, } from 'react-router-dom';
import axios from 'axios';
import Login from '../../pages/Login/Login';
import NotFound from '../../pages/NotFound/NotFound';
import PasswordRecovery from '../../pages/PasswordRecovery/PasswordRecovery';
import UsersList from '../../pages/Users/List/UsersList';
import { RootState } from '../../stores/globalStore';
import Configuration from '../../pages/Configuration/Configuration';
import Header from '../Header/Header';
import Navbar from '../Navbar/Navbar';
import './App.scss';
import { AuthHeader } from '../../utils/helpers/auth-headers';
import { setConnectedUser, setIsLogged, setLogOut, setToken, } from '../../stores/authenticationStore';
import { RequireAuth } from '../../utils/helpers/require-auth';
import UsersCreate from '../../pages/Users/Create/UsersCreate';
import UsersEdit from '../../pages/Users/Edit/UsersEdit';
import { RequireRole } from '../../utils/helpers/require-role';
import UserProfile from '../../pages/Users/Profile/UserProfile';
import Instance from '../../pages/Instances/Instance';
import RealTimeList from '../../pages/Instances/RealTime/RealTime/RealTimeList';
import RealTimeStatistics from '../../pages/Instances/RealTime/RealTimeStatistics/RealTimeStatistics';
import Global from '../../pages/Global/Global';
import Error from '../../pages/Error/Error';
import InstanceConfiguration from '../../pages/Instances/NodesConfig/NodesConfiguration';
import HistoryList from '../../pages/Instances/History/History/HistoryList';
import HistoryStatistics from '../../pages/Instances/History/HistoryStatistics/HistoryStatistics';
import InstanceAlarms from '../../pages/Instances/Alarms/Alarms';
import InstanceOverview from '../../pages/Instances/Overview/InstanceOverview';
import RealTimeParticipants from '../../pages/Instances/RealTime/RealTimeParticipants/RealTimeParticipants';
import InstancesList from '../../pages/Instances/Configuration/InstancesList';
import InstancesCreate from '../../pages/Instances/Configuration/InstancesCreate';
import InstancesEdit from '../../pages/Instances/Configuration/InstancesEdit';
import HistoryParticipants from '../../pages/Instances/History/HistoryParticipants/HistoryParticipants';
import ErrorBoundary from '../ErrorBoundaries/ErrorBoundary';
import AlertHandler from '../AlertHandler/AlertHandler';
import WorkerDetails from '../../pages/Instances/NodesConfig/WorkerDetails';
import { PexipInstance } from '../../models/PexipInstance';

function App() {
  const isLogged = useSelector((state: RootState) => state.authentication.isLogged);
  const location = useLocation();
  const { pathname } = location;

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const [noPexipInstance, setNoPexipInstance] = useState(false);
  const [instancesLoaded, setInstancesLoaded] = useState(false);
  const [pexipInstances, setPexipInstances] = useState<PexipInstance[]>([]);

  const getConnectedUser = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/user/me`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        const { data } = response;
        const storage = {
          user: data.data,
          token: AuthHeader(),
        };
        localStorage.setItem('user', JSON.stringify(storage));
        dispatch(setIsLogged({ isLogged: true }));
        dispatch(setConnectedUser({ connectedUser: data.data }));
        dispatch(setToken({ token: AuthHeader() }));
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setLogOut());
          navigate('/login', { state: { from: location }, replace: true });
        }
        console.error(error);
      });
  };

  const getPexipInstance = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/list?sort=-index`, {
      headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {},
    })
      .then((r) => {
        setPexipInstances(r.data.data);
        setInstancesLoaded(true);
        if (r.data.data && r.data.data.length > 0 && pathname === '/') {
          setNoPexipInstance(false);
          navigate('/global');
        }
      })
      .catch((error) => {
        setNoPexipInstance(true);
        setInstancesLoaded(true);
        console.error(error);
      });
  };

  useEffect(() => {
    getConnectedUser();
  }, []);

  useEffect(() => {
    getPexipInstance();
  }, [navigate]);

  return (
    <>
      <Header />
      {isLogged && !pathname.includes('/configuration/instance') && !pathname.includes('/configuration/users') && !pathname.includes('profile') && !pathname.includes('not-found') && !pathname.includes('error') && !pathname.includes('global') && !noPexipInstance && <Navbar pexipInstances={pexipInstances} />}
      {instancesLoaded && (
        <ErrorBoundary navigate={navigate}>
          <Routes>
            <Route path="/" element={pexipInstances.length > 0 ? <Navigate to="/global" /> : <Navigate to="/error?noInstance=true" />} />
            <Route path="/login" element={<Login />} />
            <Route path="/password-recovery" element={<PasswordRecovery />} />
            <Route path="/global" element={<RequireAuth><Global pexipInstances={pexipInstances} /></RequireAuth>} />
            <Route path="/instance/:instanceId" element={<RequireAuth><Instance /></RequireAuth>}>
              <Route path="/instance/:instanceId/overview" element={<RequireAuth><InstanceOverview /></RequireAuth>} />
              <Route path="/instance/:instanceId/real-time" element={<RequireAuth><RealTimeList /></RequireAuth>}>
                <Route path=":meetingId" element={<RequireAuth><RealTimeParticipants /></RequireAuth>} />
              </Route>
              <Route path="/instance/:instanceId/real-time-statistics" element={<RequireAuth><RealTimeStatistics /></RequireAuth>} />
              <Route path="/instance/:instanceId/configuration" element={<RequireAuth><InstanceConfiguration /></RequireAuth>}>
                <Route path=":workerId" element={<RequireAuth><WorkerDetails /></RequireAuth>} />
              </Route>
              {/* //TODO: reenable this route if we need global list with interractions of all participants in a pexip instance  */}
              {/* <Route path="/instance/:instanceId/participants" element={<RequireAuth><InstanceParticipants /></RequireAuth>} /> */}
              <Route path="/instance/:instanceId/alarms" element={<RequireAuth><InstanceAlarms /></RequireAuth>} />
              <Route path="/instance/:instanceId/history" element={<RequireAuth><HistoryList /></RequireAuth>}>
                <Route path=":meetingId" element={<RequireAuth><HistoryParticipants /></RequireAuth>} />
              </Route>
              <Route path="/instance/:instanceId/history-statistics" element={<RequireAuth><HistoryStatistics /></RequireAuth>} />
            </Route>
            <Route path="/configuration" element={<RequireAuth><Configuration /></RequireAuth>}>
              <Route path="/configuration/users" element={<RequireAuth><RequireRole><UsersList /></RequireRole></RequireAuth>}>
                <Route path="/configuration/users/create" element={<RequireAuth><UsersCreate /></RequireAuth>} />
                <Route path="/configuration/users/edit/:userId" element={<RequireAuth><UsersEdit /></RequireAuth>} />
              </Route>
              <Route path="/configuration/instances" element={<RequireAuth><RequireRole><InstancesList /></RequireRole></RequireAuth>}>
                <Route path="/configuration/instances/create" element={<RequireAuth><InstancesCreate /></RequireAuth>} />
                <Route path="/configuration/instances/edit/:instanceId" element={<RequireAuth><InstancesEdit /></RequireAuth>} />
              </Route>
            </Route>
            <Route path="/profile" element={<RequireAuth><UserProfile /></RequireAuth>} />
            <Route path="/error" element={<Error />} />
            <Route path="/not-found" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/not-found" />} />
          </Routes>
        </ErrorBoundary>
      )}
      <AlertHandler />
    </>
  );
}

export default App;
